import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import classNames from "classnames";

import { Dialog, Transition } from "@headlessui/react";

const classes = {
    container: classNames(
        "flex fixed z-10 inset-0 p-8"
    ),
    dialog: classNames(
        "relative overflow-hidden m-auto align-bottom sm:align-middle bg-white rounded-lg text-left shadow-xl transform transition-all",
        "flex flex-col",
        "max-h-full" // If there is a lot of content, we want Modal.Content to scroll
    ),
    header: classNames(
        "text-lg leading-6 font-medium p-6"
    ),
    content: classNames(
        'flex-grow', // if we set the height, we only want the content to expand
        'max-h-full overflow-scroll p-6 pt-0',
    ),
    footer: classNames(
        'bg-gray-50 px-6 py-4 flex flex-row-reverse space-x-reverse space-x-3 border-t'
    ),
    backdrop: classNames(
        "fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
    )
};

export default function Modal({open, onClose, initialFocus, className, children}) {

    return (
        <Transition.Root show={open} as={Fragment}>
            <Dialog
                as="div"
                static
                className={classes.container}
                initialFocus={initialFocus}
                open={open}
                onClose={onClose}
            >
                <Overlay />

                <Transition.Child
                    as={Fragment}
                    enter="ease-out duration-300"
                    enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                    enterTo="opacity-100 translate-y-0 sm:scale-100"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                    leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                >
                    <div className={classNames(className, classes.dialog)}>
                        { children }
                    </div>
                </Transition.Child>
            </Dialog>
        </Transition.Root>
    );
}

Modal.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    title: PropTypes.string,
    initialFocus: PropTypes.any,
    maxWidth: PropTypes.oneOf(['sm', 'md', 'lg', 'full']),
    children: PropTypes.any,
}

Modal.defaultProps = {
    title: null,
    initialFocus: null,
    maxWidth: 'md',
    children: null,
}

const Overlay = () => {
    return <Transition.Child
        as={Fragment}
        enter="ease-out duration-300"
        enterFrom="opacity-0"
        enterTo="opacity-100"
        leave="ease-in duration-200"
        leaveFrom="opacity-100"
        leaveTo="opacity-0"
    >
        <Dialog.Overlay className={classes.backdrop} />
    </Transition.Child>;
};

Modal.Header = ({className, children}) => (
    <Dialog.Title as="h3" className={classNames(className, classes.header)}>
        { children }
    </Dialog.Title>
);

Modal.Content = ({className, children}) => (
    <div className={classNames(className, classes.content)}>
        { children }
    </div>
);

Modal.Footer = ({children}) => (
    <div className={classes.footer}>
        { children }
    </div>
)
